import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { opsApi } from '../../api/client';
import { formatCurrency } from '../../lib/currency';

const STATUS_LABELS = { PENDING_PAYMENT: 'Pending Payment', confirmed: 'Confirmed', processing: 'Processing', shipped: 'Shipped', delivered: 'Delivered', cancelled: 'Cancelled', refunded: 'Refunded' };
const STATUS_COLORS = { PENDING_PAYMENT: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-blue-100 text-blue-800', processing: 'bg-blue-100 text-blue-800', shipped: 'bg-purple-100 text-purple-800', delivered: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800' };

function Row({ label, children }) {
  return <div className="flex gap-3 text-sm"><span className="text-gray-400 w-36 shrink-0 font-medium">{label}</span><span className="text-gray-900">{children}</span></div>;
}

export default function OpsOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { opsApi.orderDetail(id).then(r => setOrder(r.data)).finally(() => setLoading(false)); }, [id]);

  if (loading) return <div className="p-8"><div className="w-8 h-8 border-4 border-gray-200 border-t-gray-800 rounded-full animate-spin" /></div>;
  if (!order) return <div className="p-8 text-gray-500">Order not found</div>;

  const addr = order.deliveryAddressSnapshot || {};

  return (
    <div data-testid="ops-order-detail" className="p-8 max-w-4xl">
      <Link to="/ops/orders" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-6 font-medium"><ArrowLeft className="w-4 h-4" /> Orders</Link>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">#{order.orderNumber}</h1>
          <p className="text-sm text-gray-500 mt-1">{order.created_at ? new Date(order.created_at).toLocaleDateString('nl-BE', { dateStyle: 'long' }) : ''}</p>
        </div>
        <span className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-600'}`}>{STATUS_LABELS[order.status] || order.status}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Order info */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Order Info</h2>
          <Row label="Total">{formatCurrency(order.totalAmount)}</Row>
          {order.shippingCost != null && <Row label="Shipping">{order.shippingCost > 0 ? formatCurrency(order.shippingCost) : 'Free'}</Row>}
          {order.discountType && <Row label="Discount">{order.discountType} (-{formatCurrency(order.discountAmount)} excl. BTW)</Row>}
          {order.stackDiscountAmount > 0 && <Row label="Stack discount">-{formatCurrency(order.stackDiscountAmount)} excl. BTW</Row>}
          <Row label="VAT reversed">{order.vatReversed ? 'Yes (Art. 39bis)' : 'No'}</Row>
          {!order.vatReversed && order.vatAmount != null && <Row label="VAT amount">{formatCurrency(order.vatAmount)}</Row>}
        </div>
        {/* Customer */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Customer</h2>
          <Row label="Name">{order.user?.name || order.guestEmail || 'Guest'}</Row>
          <Row label="Email">{order.user?.email || order.guestEmail || '—'}</Row>
          {order.b2bPerson && <Row label="B2B">{order.b2bOrg?.companyName || 'Yes'} ({order.b2bPerson.profession})</Row>}
          {addr.street && <Row label="Address">{addr.street} {addr.number}, {addr.postalCode} {addr.city}</Row>}
        </div>
      </div>

      {/* Payment */}
      {order.payment && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Payment</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Row label="Mollie ID">{order.payment.molliePaymentId}</Row>
            <Row label="Status">{order.payment.status}</Row>
            <Row label="Amount">{formatCurrency(order.payment.amount)}</Row>
            {order.payment.paidAt && <Row label="Paid at">{new Date(order.payment.paidAt).toLocaleString('nl-BE')}</Row>}
          </div>
        </div>
      )}

      {/* Lines */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50"><h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Order Lines</h2></div>
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-400 uppercase"><tr><th className="px-5 py-2 text-left">Product</th><th className="px-5 py-2 text-center">Qty</th><th className="px-5 py-2 text-right">Unit</th><th className="px-5 py-2 text-right">Total</th></tr></thead>
          <tbody className="divide-y divide-gray-50">
            {(order.lines || []).map(l => (
              <tr key={l._id}>
                <td className="px-5 py-3 text-gray-900">{l.nameSnapshot_nl}{l.freeUnits > 0 && <span className="ml-2 text-xs text-green-600">({l.freeUnits} free)</span>}</td>
                <td className="px-5 py-3 text-center font-mono">{l.quantity}</td>
                <td className="px-5 py-3 text-right">{formatCurrency(l.effectiveUnitPrice || l.priceSnapshot)}</td>
                <td className="px-5 py-3 text-right font-semibold">{formatCurrency((l.effectiveUnitPrice || l.priceSnapshot) * l.quantity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Status history */}
      {order.statusHistory && order.statusHistory.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Status History</h2>
          <div className="space-y-2">
            {order.statusHistory.map((h, i) => (
              <div key={`${h.status}-${h.at || i}`} className="flex items-center gap-3 text-sm">
                <div className="w-2 h-2 rounded-full bg-gray-400" />
                <span className="font-semibold text-gray-700">{STATUS_LABELS[h.status] || h.status}</span>
                <span className="text-gray-400">{h.at ? new Date(h.at).toLocaleString('nl-BE') : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
